<template>
  <div class="organization-billing-info pa-10" v-if="organization">

    <div class="d-flex justify-end">
      <audit-trail-button entity-type="Organization" :entity-id="organization.id" :fields="historyFields" />
    </div>


    <!-- READ MODE -->
    <template v-if="!editing">

      <!-- Informations légales -->
      <headline>{{ $t('organization.invoice-information') }}</headline>
      <div class="grid">
        <v-row>
          <v-col class="label" sm="3">{{ $t('name') }} :</v-col>
          <v-col sm="3">
            {{ organization.invoiceContactName || '-' }}
            <v-btn class="ml-5" small @click="startEdit">
              <v-icon left>mdi-cog</v-icon>
              {{ $t('edit') }}
            </v-btn>
          </v-col>
          <v-col class="label" sm="3">{{ $t('billing-details.legal-form') }} :</v-col>
          <v-col sm="3">{{ organization.legalForm || '-' }}</v-col>
        </v-row>
        <v-row>
          <v-col class="label" sm="3">{{ $t('address') }} :</v-col>
          <v-col sm="3">
            <template v-if="organization.invoiceContactAddress">
              {{ organization.invoiceContactAddress.number }} {{ organization.invoiceContactAddress.street }},
              {{ organization.invoiceContactAddress.zipcode }} {{ organization.invoiceContactAddress.city }}
              <template v-if="organization.invoiceContactAddress.state"> - {{ organization.invoiceContactAddress.state }}</template>
              - {{ organization.invoiceContactAddress.country }}
            </template>
            <template v-else>-</template>
          </v-col>
          <v-col class="label" sm="3">{{ $t('billing-details.capital-amount') }} :</v-col>
          <v-col sm="3">{{ organization.capitalAmount != null ? organization.capitalAmount : '-' }}</v-col>
        </v-row>
        <v-row>
          <v-col class="label" sm="3">{{ $t('organization.registration-number') }} :</v-col>
          <v-col sm="3">{{ organization.registrationNumber || '-' }}</v-col>
          <v-col class="label" sm="3">{{ $t('currency') }} :</v-col>
          <v-col sm="3">{{ organization.currency || '-' }}</v-col>
        </v-row>
        <v-row>
          <v-col class="label" sm="3">{{ $t('vat-number') }} :</v-col>
          <v-col sm="3">
            <template v-if="organization.isVatExempt">{{ $t('is-vat-exempt') }}</template>
            <template v-else>
              {{ organization.vatNumber || '-' }}
              <v-tooltip bottom v-if="organization.vatNumber && !organization.isVatExempt">
                <template #activator="{on, attrs}">
                  <v-icon v-if="organization.verifiedByVies" color="success" small v-on="on" v-bind="attrs">mdi-check-decagram</v-icon>
                  <v-icon v-else color="red" small v-on="on" v-bind="attrs">mdi-alert-decagram</v-icon>
                </template>
                <div class="text-center">
                  {{ organization.verifiedByVies ? $t('organization.verified-by-vies') : $t('organization.vat-number-invalid') }}
                </div>
              </v-tooltip>
            </template>
          </v-col>
          <v-col class="label" sm="3">{{ $t('is-vat-exempt') }} :</v-col>
          <v-col sm="3"><v-switch disabled v-model="organization.isVatExempt" /></v-col>
        </v-row>
        <v-row v-if="!organization.verifiedByVies">
          <v-col class="label" sm="3">{{ $t('organization.billing-generic-account') }} :</v-col>
          <v-col sm="3"><v-switch disabled v-model="organization.useBillingGenericAccount" /></v-col>
        </v-row>
      </div>

      <!-- Contact -->
      <headline class="mt-10">Contact</headline>
      <div class="grid">
        <v-row>
          <v-col class="label" sm="3">{{ $t('email') }} :</v-col>
          <v-col sm="3">{{ organization.invoiceContactEmail || '-' }}</v-col>
          <v-col class="label" sm="3">{{ $t('phone') }} :</v-col>
          <v-col sm="3">{{ organization.invoiceContactPhone || '-' }}</v-col>
        </v-row>
        <v-row>
          <v-col class="label" sm="3">{{ $t('bcc-emails') }} :</v-col>
          <v-col sm="9">
            <template v-if="organization.invoiceContactBccEmails && organization.invoiceContactBccEmails.length">
              <v-chip small class="mr-1 mb-1" v-for="(email, index) in organization.invoiceContactBccEmails" :key="index">{{ email }}</v-chip>
            </template>
            <template v-else>-</template>
          </v-col>
        </v-row>
      </div>

      <!-- Paiement -->
      <headline class="mt-10">{{ $t('payment-method.payment-methods') }}</headline>
      <div class="grid">
        <v-row>
          <v-col class="label" sm="3">{{ $t('iban.iban') }} :</v-col>
          <v-col sm="3">{{ organization.iban || '-' }}</v-col>
          <v-col class="label" sm="3">{{ $t('iban.bic') }} :</v-col>
          <v-col sm="3">{{ organization.bic || '-' }}</v-col>
        </v-row>
        <v-row>
          <v-col class="label" sm="3">{{ $t('billing-mode.label') }} :</v-col>
          <v-col sm="3">{{ organization.billingMode ? $t('billing-mode.' + organization.billingMode) : '-' }}</v-col>
          <v-col class="label" sm="3">{{ $t('collection-method.label') }} :</v-col>
          <v-col sm="3">{{ organization.collectionMethod ? $t('collection-method.' + organization.collectionMethod) : '-' }}</v-col>
        </v-row>
      </div>

      <!-- Références -->
      <headline class="mt-10">{{ $t('references') }}</headline>
      <div class="grid">
        <v-row>
          <v-col class="label" sm="3">Ref. Sage :</v-col>
          <v-col sm="3">{{ organization.sageReference || '-' }}</v-col>
          <v-col class="label" sm="3">Ref. Horus :</v-col>
          <v-col sm="3">{{ organization.horusReference || '-' }}</v-col>
        </v-row>
      </div>

      <!-- Rôle facturation -->
      <headline class="mt-10">{{ $t('organization.invoice-role') }}</headline>
      <div class="grid">
        <v-row>
          <v-col class="label" sm="3">{{ $t('organization.can-be-invoice-recipient') }} :</v-col>
          <v-col sm="9"><v-switch disabled v-model="organization.canBeInvoiceRecipient" /></v-col>
        </v-row>
        <template v-if="!organization.canBeInvoiceRecipient">
          <v-row v-if="delegationSummary.mode === 'none'">
            <v-col class="label" sm="3">{{ $t('organization.delegation-typology.label') }} :</v-col>
            <v-col sm="9">
              <span class="text--secondary">
                <v-icon small left>mdi-domain</v-icon>
                {{ $t('organization.delegation-typology.self-billed') }}
              </span>
            </v-col>
          </v-row>
          <v-row v-else-if="delegationSummary.mode === 'all'">
            <v-col class="label" sm="3">{{ $t('organization.delegation-typology.label') }} :</v-col>
            <v-col sm="9">
              <v-chip small color="primary" outlined :href="organizationHref(delegationSummary.both)" target="_blank">
                <v-icon left small>mdi-share-variant-outline</v-icon>
                {{ $t('organization.delegation-typology.delegated-all-to', { name: delegationSummary.both.name }) }}
              </v-chip>
            </v-col>
          </v-row>
          <template v-else>
            <v-row>
              <v-col class="label" sm="3">{{ $t('organization.delegation-typology.subscription') }} :</v-col>
              <v-col sm="9">
                <v-chip v-if="delegationSummary.subscription" small color="primary" outlined :href="organizationHref(delegationSummary.subscription)" target="_blank">
                  <v-icon left small>mdi-share-variant-outline</v-icon>
                  {{ delegationSummary.subscription.name }}
                </v-chip>
                <span v-else class="text--secondary">{{ $t('organization.delegation-typology.self-billed') }}</span>
              </v-col>
            </v-row>
            <v-row>
              <v-col class="label" sm="3">{{ $t('organization.delegation-typology.usage') }} :</v-col>
              <v-col sm="9">
                <v-chip v-if="delegationSummary.usage" small color="primary" outlined :href="organizationHref(delegationSummary.usage)" target="_blank">
                  <v-icon left small>mdi-share-variant-outline</v-icon>
                  {{ delegationSummary.usage.name }}
                </v-chip>
                <span v-else class="text--secondary">{{ $t('organization.delegation-typology.self-billed') }}</span>
              </v-col>
            </v-row>
          </template>
        </template>
        <v-row v-if="organization.canBeInvoiceRecipient">
          <v-col class="label" sm="3">{{ $t('organization.delegating-orgs.label') }} :</v-col>
          <v-col sm="9">
            <v-btn small @click="$refs.delegatingOrgsModal.open()">
              <v-icon left>mdi-account-multiple-plus-outline</v-icon>
              {{ $t('organization.delegating-orgs.manage') }}
            </v-btn>
          </v-col>
        </v-row>
      </div>

      <delegating-organizations-modal ref="delegatingOrgsModal" :organization="organization" @updated="$parent.loadData()" />

      <!-- Facturation automatique -->
      <template v-if="isOperator">
        <headline class="mt-10">{{ $t('organization.auto-invoicing.title') }}</headline>
        <div class="grid">
          <v-row>
            <v-col class="label" sm="3">{{ $t('organization.auto-invoicing-enabled-label') }} :</v-col>
            <v-col sm="9">
              <v-switch disabled v-model="organization.autoInvoicingEnabled" />
              <div v-if="delegationSummary.mode !== 'none'" class="text-caption text--secondary mt-n2">
                <v-icon x-small class="mr-1">mdi-information-outline</v-icon>
                {{ $t('organization.auto-invoicing.lock-delegating') }}
              </div>
            </v-col>
          </v-row>
        </div>
      </template>

      <!-- Autorisation -->
      <headline class="mt-10">{{ $t('organization.allow-deferred-payment') }}</headline>
      <div class="grid">
        <v-row>
          <v-col class="label" sm="3">{{ $t('organization.allow-deferred-payment-short') }} :</v-col>
          <v-col sm="3"><v-switch disabled v-model="organization.empAllowDeferredPayment" /></v-col>
        </v-row>
      </div>

      <!-- Règles de bon de commande -->
      <headline class="mt-10">
        {{ $t('purchase-order-rule.title') }}
        <v-btn v-if="!poRuleEditing" small class="ml-5" @click="startEditPoRule">
          <v-icon left>mdi-cog</v-icon>
          {{ $t('edit') }}
        </v-btn>
        <audit-trail-button class="ml-2" entity-type="PurchaseOrderRule" :organization-id="organization.id" />
      </headline>

      <template v-if="!poRuleEditing">
        <div class="grid">
          <v-row>
            <v-col class="label" sm="3">{{ $t('purchase-order-rule.default-po') }} :</v-col>
            <v-col sm="9">{{ organization.invoicePurchaseOrderNumber || '-' }}</v-col>
          </v-row>
        </div>
        <v-data-table
            v-if="poRule && poRule.entries && poRule.entries.length"
            :headers="poRuleHeaders"
            :items="poRule.entries"
            hide-default-footer
            disable-pagination
            dense
            class="mt-4"
        >
          <template #item.contentType="{item}">
            {{ item.contentType ? $t('purchase-order-rule.content-types.' + item.contentType) : $t('purchase-order-rule.all-types') }}
          </template>
          <template #item.dateCriterion="{item}">
            {{ $t('purchase-order-rule.date-criteria.' + item.dateCriterion) }}
          </template>
          <template #item.dateFrom="{item}">{{ item.dateFrom | formatDate($t('format_date')) }}</template>
          <template #item.dateTo="{item}">{{ item.dateTo | formatDate($t('format_date')) }}</template>
        </v-data-table>
        <div v-if="!poRule" class="text--secondary mt-2">{{ $t('purchase-order-rule.no-rule') }}</div>
      </template>

      <template v-if="poRuleEditing">
        <v-form ref="poRuleForm" @submit.prevent="savePoRule">
          <v-row>
            <v-col cols="12" md="6">
              <form-field :label="$t('purchase-order-rule.default-po')" v-model="poRuleForm.invoicePurchaseOrderNumber" clearable />
            </v-col>
          </v-row>

          <v-data-table
              :headers="poRuleEditHeaders"
              :items="poRuleForm.entries"
              hide-default-footer
              disable-pagination
              dense
              class="mt-2"
          >
            <template #item.purchaseOrderNumber="{item}">
              <v-text-field v-model="item.purchaseOrderNumber" dense hide-details outlined />
            </template>
            <template #item.contentType="{item}">
              <v-select
                  v-model="item.contentType"
                  :items="contentTypeOptions"
                  dense hide-details outlined clearable
              />
            </template>
            <template #item.dateCriterion="{item}">
              <v-select
                  v-model="item.dateCriterion"
                  :items="dateCriterionOptions"
                  dense hide-details outlined
              />
            </template>
            <template #item.dateFrom="{item}">
              <date-picker v-model="item.dateFrom" outlined dense hide-details clearable />
            </template>
            <template #item.dateTo="{item}">
              <date-picker v-model="item.dateTo" outlined dense hide-details clearable />
            </template>
            <template #item.priority="{item}">
              <v-text-field v-model.number="item.priority" type="number" dense hide-details outlined style="width:70px" />
            </template>
            <template #item.actions="{item}">
              <v-btn icon small @click="removePoEntry(item)"><v-icon small color="red">mdi-delete</v-icon></v-btn>
            </template>
          </v-data-table>

          <v-row class="mt-2">
            <v-col>
              <v-btn small text @click="addPoEntry"><v-icon left>mdi-plus</v-icon>{{ $t('purchase-order-rule.add-entry') }}</v-btn>
            </v-col>
          </v-row>

          <v-row class="mt-4">
            <v-col class="d-flex justify-center">
              <v-btn small class="mr-5" @click="poRuleEditing = false">{{ $t('cancel') }}</v-btn>
              <v-btn small color="primary" type="submit" :loading="poRuleSaving">{{ $t('save') }}</v-btn>
            </v-col>
          </v-row>
        </v-form>
      </template>

    </template>

    <!-- EDIT MODE -->
    <template v-if="editing">
      <v-form ref="form" @submit.prevent="save">

        <!-- Informations légales -->
        <headline>{{ $t('organization.invoice-information') }}</headline>
        <v-row>
          <v-col cols="12" md="6">
            <form-field :label="$t('name')" v-model="form.invoiceContactName" clearable />
            <form-field :label="$t('number')" v-model="form.invoiceContactAddress.number" clearable />
            <form-field :label="$t('street')" v-model="form.invoiceContactAddress.street" clearable />
            <form-field :label="$t('zipcode')" v-model="form.invoiceContactAddress.zipcode" clearable />
            <form-field :label="$t('city')" v-model="form.invoiceContactAddress.city" clearable />
            <form-field :label="$t('state')" v-model="form.invoiceContactAddress.state" clearable />
            <form-field :label="$t('country')" type="available-select" input-type="country" v-model="form.country" clearable />
          </v-col>
          <v-col cols="12" md="6">
            <form-field :label="$t('billing-details.legal-form')" v-model="form.legalForm" clearable />
            <form-field :label="$t('billing-details.capital-amount')" v-model.number="form.capitalAmount" input-type="number" clearable />
            <form-field :label="$t('organization.registration-number')" v-model="form.registrationNumber" />
            <form-field :disabled="form.isVatExempt" :label="$t('vat-number')" v-model="form.vatNumber" :placeholder="form.isVatExempt ? 'N/A' : ''" clearable>
              <template #append v-if="!form.isVatExempt">
                <v-tooltip bottom v-if="organization.verifiedByVies">
                  <template #activator="{on, attrs}">
                    <v-icon color="success" v-on="on" v-bind="attrs">mdi-check-decagram</v-icon>
                  </template>
                  <div class="text-center">{{ $t('organization.verified-by-vies') }}</div>
                </v-tooltip>
                <v-tooltip bottom v-else>
                  <template #activator="{on, attrs}">
                    <v-icon color="red" v-on="on" v-bind="attrs">mdi-alert-decagram</v-icon>
                  </template>
                  <div class="text-center">{{ $t('organization.vat-number-invalid') }}</div>
                </v-tooltip>
              </template>
            </form-field>
            <form-field :label="$t('is-vat-exempt')" type="switch" v-model="form.isVatExempt" />
            <form-field v-if="!form.verifiedByVies" :label="$t('organization.billing-generic-account')" type="switch" v-model="form.useBillingGenericAccount" />
          </v-col>
        </v-row>

        <v-row>
          <v-col class="text-center">
            <v-btn :disabled="!form.vatNumber" small @click="populateAddressFromVies">
              {{ $t('organization.populate-address-from-vies') }}
            </v-btn>
          </v-col>
        </v-row>

        <!-- Contact -->
        <headline class="mt-6">Contact</headline>
        <v-row>
          <v-col cols="12" md="6">
            <form-field :label="$t('email')" v-model="form.invoiceContactEmail" input-type="email" :rules="[commonRules.email]" clearable />
            <form-field type="phone" :label="$t('phone')" v-model="form.invoiceContactPhone" clearable />
          </v-col>
          <v-col cols="12" md="6">
            <form-field type="email-array" :label="$t('bcc-emails')" v-model="form.invoiceContactBccEmails" clearable />
          </v-col>
        </v-row>

        <!-- Paiement -->
        <headline class="mt-6">{{ $t('payment-method.payment-methods') }}</headline>
        <v-row>
          <v-col cols="12" md="6">
            <form-field :label="$t('iban.iban')" v-model="form.iban" clearable />
            <form-field :label="$t('iban.bic')" v-model="form.bic" clearable />
          </v-col>
          <v-col cols="12" md="6">
            <form-field :label="$t('billing-mode.label')" v-model="form.billingMode" type="select" :items="billingModeOptions" clearable />
            <form-field :label="$t('collection-method.label')" v-model="form.collectionMethod" type="select" :items="collectionMethodOptions" clearable />
          </v-col>
        </v-row>

        <!-- Références -->
        <headline class="mt-6">{{ $t('references') }}</headline>
        <v-row>
          <v-col cols="12" md="6">
            <form-field label="Ref. Sage" v-model="form.sageReference" clearable />
          </v-col>
          <v-col cols="12" md="6">
            <form-field label="Ref. Horus" v-model="form.horusReference" clearable />
          </v-col>
        </v-row>
        <!-- Rôle facturation -->
        <headline class="mt-6">{{ $t('organization.invoice-role') }}</headline>
        <v-row>
          <v-col cols="12">
            <form-field
                :label="$t('organization.can-be-invoice-recipient')"
                type="switch"
                v-model="form.canBeInvoiceRecipient"
                :label-tooltip="$t('organization.can-be-invoice-recipient-hint')"
                col-field="9"
            />
          </v-col>
        </v-row>

        <!-- Délégation de facturation (par typologie) -->
        <template v-if="!form.canBeInvoiceRecipient">
          <v-row>
            <v-col cols="12">
              <form-field
                  :label="$t('organization.delegation-typology.same-recipients')"
                  type="switch"
                  v-model="form.delegationSameRecipients"
                  :disabled="isInvoiceRecipientForOthers"
                  :label-tooltip="$t('organization.delegation-typology.same-recipients-hint')"
                  col-field="9"
              />
            </v-col>
          </v-row>
          <v-row v-if="form.delegationSameRecipients">
            <v-col cols="12">
              <form-field
                  type="organization-select"
                  :label="$t('organization.delegation-typology.recipient-both')"
                  v-model="form.subscriptionInvoiceRecipientOrganizationId"
                  :disabled="isInvoiceRecipientForOthers"
                  :hint="isInvoiceRecipientForOthers ? $t('organization.invoice-recipient-inbound-disabled') : $t('organization.invoice-recipient-hint')"
                  :filter="invoiceRecipientFilter"
                  :can-be-invoice-recipient="true"
                  selection-with-no-extra
                  clearable
                  col-field="9"
                  @input="form.usageInvoiceRecipientOrganizationId = form.subscriptionInvoiceRecipientOrganizationId"
              />
            </v-col>
          </v-row>
          <template v-else>
            <v-row>
              <v-col cols="12">
                <form-field
                    type="organization-select"
                    :label="$t('organization.delegation-typology.subscription-recipient')"
                    v-model="form.subscriptionInvoiceRecipientOrganizationId"
                    :disabled="isInvoiceRecipientForOthers"
                    :filter="invoiceRecipientFilter"
                    :can-be-invoice-recipient="true"
                    selection-with-no-extra
                    clearable
                    col-field="9"
                />
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="12">
                <form-field
                    type="organization-select"
                    :label="$t('organization.delegation-typology.usage-recipient')"
                    v-model="form.usageInvoiceRecipientOrganizationId"
                    :disabled="isInvoiceRecipientForOthers"
                    :filter="invoiceRecipientFilter"
                    :can-be-invoice-recipient="true"
                    selection-with-no-extra
                    clearable
                    col-field="9"
                />
              </v-col>
            </v-row>
          </template>
        </template>

        <!-- Facturation automatique -->
        <template v-if="isOperator">
          <headline class="mt-6">{{ $t('organization.auto-invoicing.title') }}</headline>
          <v-row>
            <v-col cols="12">
              <form-field
                  :label="$t('organization.auto-invoicing-enabled-label')"
                  type="switch"
                  v-model="form.autoInvoicingEnabled"
                  :disabled="!form.autoInvoicingEnabled && !autoInvoicingCanEnable"
                  col-field="9"
              />
            </v-col>
          </v-row>
          <v-row v-if="autoInvoicingLockHint" dense>
            <v-col cols="12" offset-sm="3" sm="9">
              <div
                  class="text-caption"
                  :class="autoInvoicingCanEnable || form.autoInvoicingEnabled ? 'text--secondary' : 'orange--text text--darken-2'"
              >
                <v-icon
                    v-if="!autoInvoicingCanEnable && !form.autoInvoicingEnabled"
                    x-small
                    color="orange darken-2"
                    class="mr-1"
                >
                  mdi-alert-outline
                </v-icon>
                {{ autoInvoicingLockHint }}
              </div>
            </v-col>
          </v-row>
        </template>

        <!-- Autorisation -->
        <headline class="mt-6">{{ $t('organization.allow-deferred-payment') }}</headline>
        <v-row>
          <v-col cols="12" md="6">
            <form-field :label="$t('organization.allow-deferred-payment-short')" type="switch" v-model="form.empAllowDeferredPayment" />
          </v-col>
        </v-row>

        <v-row class="mt-4">
          <v-col class="d-flex justify-center">
            <v-btn small class="mr-5" @click="editing = false">{{ $t('cancel') }}</v-btn>
            <v-btn small color="primary" type="submit" :loading="saving">{{ $t('save') }}</v-btn>
          </v-col>
        </v-row>
      </v-form>
    </template>

  </div>
</template>

<script>
import AuditTrailButton from "@elements/AuditTrailButton.vue";

/** Les champs de ce formulaire, pour son historique. */
const BILLING_FIELDS = ['invoiceContactName','invoiceContactAddress','invoiceContactEmail','invoiceContactBccEmails','invoiceContactPhone','invoicePurchaseOrderNumber','country','legalForm','registrationNumber','vatNumber','isVatExempt','verifiedByVies','useBillingGenericAccount','iban','bic','billingMode','collectionMethod','sageReference','horusReference','canBeInvoiceRecipient','subscriptionInvoiceRecipientOrganizationId','usageInvoiceRecipientOrganizationId','autoInvoicingEnabled','empAllowDeferredPayment']
import OrganizationRepository from "@repository/OrganizationRepository";
import PurchaseOrderRuleRepository from "@repository/PurchaseOrderRuleRepository";
import DatePicker from "@blocks/DatePicker";
import RulesMixin from "@mixins/RulesMixin";
import DelegatingOrganizationsModal from "@pages/Organization/Detail/DelegatingOrganizationsModal.vue";

export default {
  components: {AuditTrailButton,  DatePicker, DelegatingOrganizationsModal },
  mixins: [RulesMixin],

  data() {
    return {
      editing: false,
      saving: false,
      form: {},
      poRule: null,
      poRuleEditing: false,
      poRuleSaving: false,
      poRuleForm: {
        invoicePurchaseOrderNumber: null,
        entries: [],
      },
    }
  },

  mounted() {
    this.loadPoRule()
  },

  watch: {
    'form.isVatExempt'(val) {
      if (val) {
        this.form.vatNumber = null
        this.form.verifiedByVies = false
      }
    },

    'form.country'() {
      if (this.form.invoiceContactAddress) {
        this.form.invoiceContactAddress.country = this.form.country
      }
    },

    // Delegation and auto-invoicing are mutually exclusive. As soon as a recipient is picked,
    // mirror the backend's silent disable so the toggle reflects reality and the PUT never tries
    // to send `autoInvoicingEnabled: true` alongside a delegation (which the back rejects).
    autoInvoicingDelegated(delegated) {
      if (delegated && this.form && this.form.autoInvoicingEnabled) {
        this.form.autoInvoicingEnabled = false
      }
    },
  },

  computed: {

    historyFields() {

      return BILLING_FIELDS

    },
    organization() {
      return this.$parent.organization ?? null
    },

    isOperator() {
      return !!(this.currentUser && this.currentUser.isZE && this.currentUser.permissions
          && this.currentUser.permissions.includes('OPERATOR_WRITE'))
    },

    autoInvoicingDelegated() {
      return !!(this.form && (
          this.form.subscriptionInvoiceRecipientOrganizationId
          || this.form.usageInvoiceRecipientOrganizationId
      ))
    },

    autoInvoicingCanEnable() {
      // Auto-invoicing and billing delegation are mutually exclusive: a delegating org can never
      // be auto-invoiced (the backend rejects it with `auto-invoicing-forbidden-when-delegating`).
      if (this.autoInvoicingDelegated) return false
      const f = this.form || {}
      const name = f.invoiceContactName || (this.organization && this.organization.name)
      if (!name) return false
      if (!f.vatNumber && !f.isVatExempt) return false
      const addr = f.invoiceContactAddress
      if (!addr || !addr.street || !addr.zipcode || !addr.city) return false
      return true
    },

    autoInvoicingLockHint() {
      // Delegation lock takes precedence: it's the reason the toggle is forced off & disabled.
      if (this.autoInvoicingDelegated) {
        return this.$t('organization.auto-invoicing.lock-delegating')
      }
      if (this.autoInvoicingCanEnable || (this.form && this.form.autoInvoicingEnabled)) {
        return this.$t('organization.auto-invoicing-enabled-help')
      }
      return this.$t('organization.auto-invoicing.lock-incomplete')
    },

    isInvoiceRecipientForOthers() {
      return !!(this.organization && this.organization.isInvoiceRecipientForOthers)
    },

    delegationSummary() {
      const o = this.organization || {}
      // Fall back to the legacy embed for orgs not yet migrated by the backfill —
      // pre-migration, both new embeds may be null while invoiceRecipient still carries the value.
      const sub = o.subscriptionInvoiceRecipient || o.invoiceRecipient || null
      const usg = o.usageInvoiceRecipient || o.invoiceRecipient || null
      if (!sub && !usg) return { mode: 'none' }
      if (sub && usg && sub.id === usg.id) return { mode: 'all', both: sub }
      return { mode: 'mixed', subscription: sub, usage: usg }
    },

    invoiceRecipientFilter() {
      const currentId = this.organization?.id
      const country = this.form?.country ?? this.organization?.country
      const currency = this.organization?.currency
      const subId = this.form?.subscriptionInvoiceRecipientOrganizationId
      const usgId = this.form?.usageInvoiceRecipientOrganizationId
      return (o) => {
        // Always keep the currently selected option visible (otherwise it'd disappear post-pick).
        if (o.id === subId || o.id === usgId) return true
        if (o.id === currentId) return false
        if (country && o.country && o.country !== country) return false
        if (currency && o.currency && o.currency !== currency) return false
        // Chain rule (typology-agnostic): disqualify any org that already delegates anything.
        if (o.subscriptionInvoiceRecipient || o.subscriptionInvoiceRecipientOrganizationId) return false
        if (o.usageInvoiceRecipient || o.usageInvoiceRecipientOrganizationId) return false
        if (o.invoiceRecipient || o.invoiceRecipientOrganizationId) return false
        return true
      }
    },

    billingModeOptions() {
      return [
        { text: this.$t('billing-mode.pay-per-use'), value: 'pay-per-use' },
        { text: this.$t('billing-mode.monthly'), value: 'monthly' },
        { text: this.$t('billing-mode.threshold'), value: 'threshold', disabled: true },
      ]
    },

    collectionMethodOptions() {
      return [
        { text: this.$t('collection-method.auto-debit'), value: 'auto-debit' },
        { text: this.$t('collection-method.bank-transfer'), value: 'bank-transfer' },
      ]
    },

    contentTypeOptions() {
      return [
        { text: this.$t('purchase-order-rule.all-types'), value: null },
        { text: this.$t('purchase-order-rule.content-types.subscription'), value: 'subscription' },
        { text: this.$t('purchase-order-rule.content-types.usage'), value: 'usage' },
      ]
    },

    dateCriterionOptions() {
      return [
        { text: this.$t('purchase-order-rule.date-criteria.service_date'), value: 'service_date' },
        { text: this.$t('purchase-order-rule.date-criteria.billing_date'), value: 'billing_date' },
      ]
    },

    poRuleHeaders() {
      return [
        { text: this.$t('invoice.purchase-order-number'), value: 'purchaseOrderNumber' },
        { text: this.$t('purchase-order-rule.content-type'), value: 'contentType' },
        { text: this.$t('purchase-order-rule.date-criterion'), value: 'dateCriterion' },
        { text: this.$t('purchase-order-rule.date-from'), value: 'dateFrom' },
        { text: this.$t('purchase-order-rule.date-to'), value: 'dateTo' },
        { text: this.$t('purchase-order-rule.priority'), value: 'priority' },
      ]
    },

    poRuleEditHeaders() {
      return [
        ...this.poRuleHeaders,
        { text: '', value: 'actions', sortable: false, width: 50 },
      ]
    },
  },

  methods: {
    organizationHref(recipient) {
      if (!recipient?.id) return undefined
      return this.$router.resolve({name: 'organizations.detail', params: {id: recipient.id}}).href
    },

    startEdit() {
      this.form = {
        invoiceContactName: this.organization.invoiceContactName,
        invoiceContactEmail: this.organization.invoiceContactEmail,
        invoiceContactPhone: this.organization.invoiceContactPhone,
        invoiceContactBccEmails: this.organization.invoiceContactBccEmails ? [...this.organization.invoiceContactBccEmails] : [],
        invoiceContactAddress: { ...(this.organization.invoiceContactAddress || { number: '', street: '', city: '', zipcode: '', state: '', country: '' }) },
        invoicePurchaseOrderNumber: this.organization.invoicePurchaseOrderNumber,
        legalForm: this.organization.legalForm,
        capitalAmount: this.organization.capitalAmount,
        registrationNumber: this.organization.registrationNumber,
        vatNumber: this.organization.vatNumber,
        isVatExempt: this.organization.isVatExempt,
        verifiedByVies: this.organization.verifiedByVies,
        useBillingGenericAccount: this.organization.useBillingGenericAccount,
        iban: this.organization.iban,
        bic: this.organization.bic,
        sageReference: this.organization.sageReference,
        horusReference: this.organization.horusReference,
        country: this.organization.country,
        billingMode: this.organization.billingMode,
        collectionMethod: this.organization.collectionMethod,
        empAllowDeferredPayment: this.organization.empAllowDeferredPayment,
        subscriptionInvoiceRecipientOrganizationId: this.organization.subscriptionInvoiceRecipient?.id
            ?? this.organization.invoiceRecipient?.id
            ?? null,
        usageInvoiceRecipientOrganizationId: this.organization.usageInvoiceRecipient?.id
            ?? this.organization.invoiceRecipient?.id
            ?? null,
        delegationSameRecipients: this.computeSameRecipientsInitial(),
        canBeInvoiceRecipient: !!this.organization.canBeInvoiceRecipient,
        autoInvoicingEnabled: !!this.organization.autoInvoicingEnabled,
      }
      this.editing = true
    },

    computeSameRecipientsInitial() {
      const subId = this.organization.subscriptionInvoiceRecipient?.id ?? null
      const usgId = this.organization.usageInvoiceRecipient?.id ?? null
      // Both null (no delegation yet) → start in "same" mode for a simpler UX.
      // Both set to the same id → "same" mode. Otherwise the user explicitly has a partial setup.
      return subId === usgId
    },

    save() {
      if (!this.$refs.form.validate()) return
      this.saving = true
      const data = { ...this.form }
      delete data.verifiedByVies
      delete data.delegationSameRecipients
      if (this.organization.verifiedByVies) {
        data.useBillingGenericAccount = false
      }
      // When the toggle is ON, mirror the value so we never accidentally send a partial setup.
      if (this.form.delegationSameRecipients) {
        data.usageInvoiceRecipientOrganizationId = data.subscriptionInvoiceRecipientOrganizationId
      }
      if (this.isInvoiceRecipientForOthers || data.canBeInvoiceRecipient) {
        data.subscriptionInvoiceRecipientOrganizationId = null
        data.usageInvoiceRecipientOrganizationId = null
      }
      // Drop the legacy field: as soon as one of the two new ones is non-null the back ignores
      // it, and we want a single source of truth. The back accepts the payload without it.
      delete data.invoiceRecipientOrganizationId
      if (!this.isOperator) {
        delete data.autoInvoicingEnabled
      } else if (this.autoInvoicingDelegated) {
        // Mutual exclusion safety net: a delegating org can't be auto-invoiced.
        data.autoInvoicingEnabled = false
      }
      OrganizationRepository.updateBillingInfo(this.organization.id, data).then(() => {
        this.saving = false
        this.editing = false
        this.$parent.loadData()
      }).catch(e => {
        this.saving = false
        this.notifyError(e)
      })
    },

    populateAddressFromVies() {
      this.showLoading(true)
      OrganizationRepository.viesVerify(this.form.vatNumber).then(result => {
        if (result.valid) {
          if (result.name) {
            this.form.invoiceContactName = result.name
          }
          if (result.geoAddress) {
            this.form.invoiceContactAddress = {
              number: result.geoAddress.number,
              street: result.geoAddress.street,
              zipcode: result.geoAddress.zipcode,
              city: result.geoAddress.city,
              country: result.geoAddress.country,
            }
          }
        }
        this.showLoading(false)
      }).catch(err => {
        this.showLoading(false)
        this.notifyError(err)
      })
    },

    loadPoRule() {
      if (!this.organization) return
      PurchaseOrderRuleRepository.search({ organizationId: this.organization.id }).then(result => {
        this.poRule = result.items && result.items.length > 0 ? result.items[0] : null
      }).catch(err => {
        this.notifyError(err)
      })
    },

    startEditPoRule() {
      this.poRuleForm = {
        invoicePurchaseOrderNumber: this.organization.invoicePurchaseOrderNumber || null,
        entries: this.poRule && this.poRule.entries
            ? this.poRule.entries.map(e => ({ ...e }))
            : [],
      }
      this.poRuleEditing = true
    },

    addPoEntry() {
      this.poRuleForm.entries.push({
        purchaseOrderNumber: '',
        contentType: null,
        dateCriterion: 'service_date',
        dateFrom: null,
        dateTo: null,
        priority: 0,
      })
    },

    removePoEntry(item) {
      const idx = this.poRuleForm.entries.indexOf(item)
      if (idx > -1) this.poRuleForm.entries.splice(idx, 1)
    },

    savePoRule() {
      this.poRuleSaving = true
      const data = {
        organizationId: this.organization.id,
        invoicePurchaseOrderNumber: this.poRuleForm.invoicePurchaseOrderNumber,
        entries: this.poRuleForm.entries.map(e => ({
          purchaseOrderNumber: e.purchaseOrderNumber,
          contentType: e.contentType || null,
          dateCriterion: e.dateCriterion,
          dateFrom: e.dateFrom || null,
          dateTo: e.dateTo || null,
          priority: e.priority || 0,
        })),
      }

      const promise = this.poRule
          ? PurchaseOrderRuleRepository.update(this.poRule.id, data)
          : PurchaseOrderRuleRepository.create(data)

      promise.then(() => {
        this.poRuleSaving = false
        this.poRuleEditing = false
        this.loadPoRule()
        this.$parent.loadData() // Refresh org to update invoicePurchaseOrderNumber
      }).catch(err => {
        this.poRuleSaving = false
        this.notifyError(err)
      })
    },
  },
}
</script>
